import { Button } from "@material-ui/core";
import ConfigForm, {
  ConfigInput,
  FormOption,
} from "components/Layout/ConfigForm";
import DottedOutlineButton from "components/Layout/DottedOutlineButton";
import ItemCard from "components/Layout/ItemCard";
import { StandardContainer } from "components/Layout/StandardStyledComponents";
import React, { Fragment, useState } from "react";
import styled from "styled-components";

const Container = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  padding: 12px 6px;
  justify-content: space-between;
  width: 40%;
`;

export interface ScheduleInput {
  days: string[];
  time: string;
}

export interface OnceOffInput {
  dateTime: string;
}

interface ScheduleCreatorProps {
  schedules: ScheduleInput[];
  onceOffs: OnceOffInput[];
  onScheduleSubmit: (schedule: ScheduleInput) => void;
  onOnceOffSubmit: (onceOff: OnceOffInput) => void;
}

interface ScheduleCreatorState {
  editing: boolean;
  creatingSchedule: boolean;
  creatingOnceOff: boolean;
  selectedDays: string[];
  currentSchedule: ScheduleInput;
  currentOnceOff: OnceOffInput;
}

const DEFAULT_STATE: ScheduleCreatorState = {
  editing: false,
  creatingSchedule: false,
  creatingOnceOff: false,
  selectedDays: [],
  currentSchedule: { days: [], time: "07:00" },
  currentOnceOff: { dateTime: "" },
};
const days: FormOption[] = [
  { value: "Monday", label: "Monday" },
  { value: "Tuesday", label: "Tuesday" },
  { value: "Wednesday", label: "Wednesday" },
  { value: "Thursday", label: "Thursday" },
  { value: "Friday", label: "Friday" },
  { value: "Saturday", label: "Saturday" },
  { value: "Sunday", label: "Sunday" },
];

export default function ScheduleCreator({
  schedules,
  onceOffs,
  onOnceOffSubmit,
  onScheduleSubmit,
}: ScheduleCreatorProps) {
  const [state, setState] = useState<ScheduleCreatorState>(DEFAULT_STATE);
  const handleScheduleAddClick = () => {
    setState((prevState) => ({
      ...prevState,
      creatingSchedule: !prevState.creatingSchedule,
      creatingOnceOff: false,
    }));
  };

  const handleOnceOffAddClick = () => {
    setState((prevState) => ({
      ...prevState,
      creatingSchedule: false,
      creatingOnceOff: !prevState.creatingOnceOff,
    }));
  };

  const onCancel = () => {
    setState((prevState) => ({
      ...prevState,
      creatingSchedule: false,
      creatingOnceOff: false,
    }));
  };

  const _onScheduleSubmit = () => {
    onScheduleSubmit(state.currentSchedule);
    setState({
      ...state,
      editing: false,
      creatingSchedule: false,
      creatingOnceOff: false,
      selectedDays: [],
      currentSchedule: { days: [], time: "07:00" },
      currentOnceOff: { dateTime: "" },
    });
  };

  const _onOnceOffSubmit = () => {
    onOnceOffSubmit(state.currentOnceOff);
    setState({
      ...state,
      editing: false,
      creatingSchedule: false,
      creatingOnceOff: false,
      selectedDays: [],
      currentSchedule: { days: [], time: "07:00" },
      currentOnceOff: { dateTime: "" },
    });
  };
  const handleDaysChange = (id: any) => {
    if (state.currentSchedule.days.includes(id)) {
      const newDays = state.currentSchedule.days.filter((rts) => {
        return rts !== id;
      });
      setState((prevState) => ({
        ...prevState,
        currentSchedule: { ...prevState.currentSchedule, days: newDays },
      }));
    } else {
      const newDays = state.currentSchedule.days;
      newDays.push(id);
      setState((prevState) => ({
        ...prevState,
        currentSchedule: { ...prevState.currentSchedule, days: newDays },
      }));
    }
  };
  const handleScheduleTimeChange = (event: any) => {
    setState((prevState) => ({
      ...prevState,
      currentSchedule: {
        ...prevState.currentSchedule,
        time: event.target.value,
      },
    }));
  };
  const handleOnceOffDateTimeChange = (event: any) => {
    setState((prevState) => ({
      ...prevState,
      currentOnceOff: {
        ...prevState.currentOnceOff,
        dateTime: event.target.value,
      },
    }));
  };
  const scheduleAddInputs: ConfigInput[] = [
    {
      title: "Select Days",
      type: "checklist",
      onChange: () => {},
      onOptionClick: handleDaysChange,
      xs: 12,
      sm: 6,
      id: "days",
      placeholder: "Select days of the week",
      options: days,
      selectedOptions: state.currentSchedule.days,
    },
    {
      title: "Select Time",
      onChange: handleScheduleTimeChange,
      xs: 12,
      sm: 6,
      id: "selectScheduleTime",
      type: "time",
      value: state.currentSchedule.time,
    },
  ];

  const schedulesList = !state.creatingSchedule ? (
    <StandardContainer>
      {schedules.map((schedule, index) => {
        let title = schedule.days.join(", ") + " - " + schedule.time;
        return <ItemCard title={title} key={index} />;
      })}
    </StandardContainer>
  ) : null;
  const onceOffsList = !state.creatingOnceOff ? (
    <StandardContainer>
      {onceOffs.map((onceOff, index) => {
        let title = onceOff.dateTime;
        return <ItemCard title={title} key={index} />;
      })}
    </StandardContainer>
  ) : null;
  const sheduleAdd = state.creatingSchedule ? (
    <ConfigForm
      title="Select Days and a Time"
      description="For different times add more schedules"
      inputs={scheduleAddInputs}
    >
      <ButtonsContainer>
        <Button variant="contained" color="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={_onScheduleSubmit}>
          Add
        </Button>
      </ButtonsContainer>
    </ConfigForm>
  ) : (
    <DottedOutlineButton
      title="Add Repeating Schedule"
      handleClick={handleScheduleAddClick}
    />
  );
  const onceOffAddInputs: ConfigInput[] = [
    {
      title: "Select Date & Time",
      onChange: handleOnceOffDateTimeChange,
      xs: 12,
      sm: 12,
      id: "selectOnceOffDateTime",
      type: "dateTime",
      value: state.currentOnceOff.dateTime,
    },
  ];
  const onceOffAdd = state.creatingOnceOff ? (
    <ConfigForm title="Select a time and Date" inputs={onceOffAddInputs}>
      <ButtonsContainer>
        <Button variant="contained" color="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={_onOnceOffSubmit}>
          Add
        </Button>
      </ButtonsContainer>
    </ConfigForm>
  ) : (
    <DottedOutlineButton
      title="Add Once Off Time/Date"
      handleClick={handleOnceOffAddClick}
    />
  );

  return (
    <Fragment>
      {schedulesList}

      <Container>{sheduleAdd}</Container>

      {onceOffsList}

      <Container>{onceOffAdd}</Container>
    </Fragment>
  );
}
